import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useCreateFact,
  useDeleteFact,
  useGetAllFactsByLead,
  usePublishFact,
  useResetFactVotes,
  useUnpublishFact,
} from '@/application/services/fact.service'
import { useAuthStore } from '@/application/stores/auth.store'
import type { Fact } from '@/domains/types'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/common/Dialog'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { ImageField } from '@/ui/components/forms/ImageField'
import { Input } from '@/ui/components/forms/Input'
import { EditFactDialog } from '@/ui/components/facts/EditFactDialog'
import { ConfirmNoSourceDialog } from '@/ui/components/leads/ConfirmNoSourceDialog'

import { FactTile } from './FactTile'
import { SortableFactTile } from './SortableFactTile'
import { useFactReorder } from './useFactReorder'

const makeFactSchema = (translate: (key: string, opts?: Record<string, unknown>) => string) =>
  z.object({
    title: z
      .string()
      .min(1, translate('validation.required'))
      .max(200, translate('validation.max_chars', { count: 200 })),
    description: z
      .string()
      .min(1, translate('validation.description_required'))
      .max(20000, translate('validation.max_chars', { count: 20000 })),
    // Optional: an empty string means "no source" and only triggers a
    // confirmation on creation.
    url: z.string().url(translate('validation.url_invalid')).or(z.literal('')).optional(),
  })

type FactFormData = z.infer<ReturnType<typeof makeFactSchema>>

/**
 * Facts gallery for a lead: header with add button, full tile list,
 * and the "add fact" dialog with its react-hook-form + zod schema.
 *
 * Owns: facts fetch, create/delete mutations, dialog open state, image-url
 * state, and the fact form. Co-locates the internal `FactTile` and zod
 * schema since they are not reused.
 */
export function LeadFactsSection({
  leadId,
  investigationId,
}: {
  leadId: string
  investigationId: string
}) {
  const { t } = useTranslation()
  const factSchema = useMemo(() => makeFactSchema(t), [t])

  const [factDialogOpen, setFactDialogOpen] = useState(false)
  const [factImageUrl, setFactImageUrl] = useState('')
  // Holds the submitted values while the "no source" confirmation is pending.
  const [pendingSourcelessFact, setPendingSourcelessFact] = useState<FactFormData | null>(null)
  const [isConfirmingSourceless, setIsConfirmingSourceless] = useState(false)

  const { data: facts, isLoading: isLoadingFacts } = useGetAllFactsByLead(leadId)
  const { mutateAsync: createFact } = useCreateFact(leadId)
  const { mutateAsync: deleteFact } = useDeleteFact(leadId)
  const { mutateAsync: publishFact } = usePublishFact(leadId, investigationId)
  const { mutateAsync: unpublishFact } = useUnpublishFact(leadId, investigationId)
  const resetVotesMutation = useResetFactVotes(leadId)
  const { user } = useAuthStore()

  const [editFact, setEditFact] = useState<Fact | null>(null)

  const canEditFact = (fact: Fact): boolean => {
    if (!user) return false
    if (user.role === 'streamer' || user.role === 'admin') return true
    return fact.submittedByUserId != null && fact.submittedByUserId === user.id
  }

  const {
    register: registerFact,
    control: controlFact,
    handleSubmit: handleFactSubmit,
    reset: resetFactForm,
    formState: { errors: factErrors, isSubmitting: isFactSubmitting },
  } = useForm<FactFormData>({ resolver: zodResolver(factSchema) })

  const submitFact = async (data: FactFormData) => {
    await createFact({ ...data, imageUrl: factImageUrl || null })
    resetFactForm()
    setFactDialogOpen(false)
    setFactImageUrl('')
    toast.success(t('lead_detail.add_fact_success'))
  }

  const onFactSubmit = async (data: FactFormData) => {
    // A fact without a verifiable source is allowed, but never by accident.
    if (!data.url?.trim()) {
      setPendingSourcelessFact(data)
      return
    }
    await submitFact(data)
  }

  const handleConfirmSourceless = async () => {
    if (!pendingSourcelessFact) return
    setIsConfirmingSourceless(true)
    try {
      await submitFact(pendingSourcelessFact)
      setPendingSourcelessFact(null)
    } finally {
      setIsConfirmingSourceless(false)
    }
  }

  const handlePublishFact = async (factId: string) => {
    try {
      await publishFact(factId)
      toast.success(t('lead_detail.fact_publish_success'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.fact_publish_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const handleUnpublishFact = async (factId: string) => {
    try {
      await unpublishFact(factId)
      toast.success(t('lead_detail.fact_unpublish_success'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.fact_unpublish_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  const handleDeleteFact = async (factId: string) => {
    if (window.confirm(t('lead_detail.fact_delete_confirm'))) {
      try {
        await deleteFact(factId)
        toast.success(t('lead_detail.fact_deleted'))
      } catch (error: unknown) {
        toast.error(
          t('lead_detail.fact_delete_failed', {
            error: error instanceof Error ? error.message : String(error),
          }),
        )
      }
    }
  }

  const handleResetVotes = async (factId: string) => {
    if (!window.confirm(t('lead_detail.fact_reset_votes_confirm'))) return
    try {
      await resetVotesMutation.mutateAsync(factId)
      toast.success(t('lead_detail.fact_reset_votes_success'))
    } catch (error: unknown) {
      toast.error(
        t('lead_detail.fact_reset_votes_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  // Facts already come back ordered by `position` from the API — no client-side
  // re-sorting, so the manual order set via drag & drop (or the API) is honoured as-is.
  const sortedFacts = useMemo(() => (facts ?? []).filter((fact) => !fact.originalSource), [facts])

  const canReorder = user?.role === 'streamer' || user?.role === 'admin'

  const { orderedFacts, handleDragEnd } = useFactReorder({
    leadId,
    facts: sortedFacts,
    onSuccess: () => toast.success(t('lead_detail.fact_reorder_success')),
    onError: (message) => toast.error(t('lead_detail.fact_reorder_failed', { error: message })),
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl font-bold flex items-center gap-2">
          {t('lead_detail.facts')}
          <Badge variant="secondary">{sortedFacts.length}</Badge>
        </h2>
        <Button
          size="sm"
          onClick={() => setFactDialogOpen(true)}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          {t('lead_detail.add_fact_button')}
        </Button>
      </div>

      {isLoadingFacts ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_item, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-lg overflow-hidden flex h-24"
            >
              <Skeleton className="w-24 sm:w-32 shrink-0" />
              <div className="flex-1 p-4 space-y-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : !facts || facts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-lg text-sm">
          {t('lead_detail.no_facts')}
        </div>
      ) : canReorder ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={orderedFacts.map((fact) => fact.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {orderedFacts.map((fact) => (
                <SortableFactTile
                  key={fact.id}
                  fact={fact}
                  leadId={leadId}
                  canEdit={canEditFact(fact)}
                  onEdit={() => setEditFact(fact)}
                  onDelete={() => handleDeleteFact(fact.id)}
                  onPublish={!fact.published ? () => handlePublishFact(fact.id) : undefined}
                  onUnpublish={fact.published ? () => handleUnpublishFact(fact.id) : undefined}
                  onResetVotes={() => handleResetVotes(fact.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-3">
          {orderedFacts.map((fact) => (
            <FactTile
              key={fact.id}
              fact={fact}
              leadId={leadId}
              canEdit={canEditFact(fact)}
              onEdit={() => setEditFact(fact)}
              onDelete={() => handleDeleteFact(fact.id)}
              onPublish={undefined}
              onUnpublish={undefined}
              onResetVotes={undefined}
            />
          ))}
        </div>
      )}

      {/* ─── Add Fact Dialog ─── */}
      <Dialog
        open={factDialogOpen}
        onOpenChange={(open) => {
          setFactDialogOpen(open)
          if (!open) resetFactForm()
        }}
      >
        <DialogContent size="wide">
          <DialogHeader>
            <DialogTitle className="font-serif">{t('lead_detail.add_fact_title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFactSubmit(onFactSubmit)} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {t('lead_detail.fact_title_label')} <span className="text-destructive">*</span>
              </label>
              <Input
                {...registerFact('title')}
                placeholder={t('lead_detail.fact_title_placeholder')}
              />
              {factErrors.title && (
                <p className="text-destructive text-xs">{factErrors.title.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {t('lead_detail.fact_description_label')}{' '}
                <span className="text-destructive">*</span>
              </label>
              <Controller
                name="description"
                control={controlFact}
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('lead_detail.fact_description_placeholder')}
                    compact
                    onUploadError={(message) => toast.error(message)}
                  />
                )}
              />
              {factErrors.description && (
                <p className="text-destructive text-xs">{factErrors.description.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('lead_detail.fact_url_label')}</label>
              <Input
                {...registerFact('url')}
                type="url"
                placeholder={t('lead_detail.fact_url_placeholder')}
              />
              {factErrors.url && (
                <p className="text-destructive text-xs">{factErrors.url.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('lead_detail.fact_image_label')}</label>
              <ImageField value={factImageUrl} onChange={(imageUrl) => setFactImageUrl(imageUrl)} />
            </div>
            <DialogFooter className="pt-2">
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isFactSubmitting}>
                  {t('common.cancel')}
                </Button>
              </DialogClose>
              <Button type="submit" isLoading={isFactSubmitting}>
                {t('lead_detail.add_fact_submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmNoSourceDialog
        open={pendingSourcelessFact !== null}
        onOpenChange={(open) => {
          if (!open) setPendingSourcelessFact(null)
        }}
        onConfirm={handleConfirmSourceless}
        isSubmitting={isConfirmingSourceless}
      />

      <EditFactDialog
        fact={editFact}
        leadId={leadId}
        open={editFact !== null}
        onOpenChange={(open) => {
          if (!open) setEditFact(null)
        }}
      />
    </div>
  )
}
